// @ts-nocheck
// Exact live (2026-10-15) version restored from the production sourcemap.
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, Grid, IconButton, Typography } from '@mui/material';
import { Add, CloseCircle, Minus } from 'iconsax-react';
import { WidgetHeader } from '@ui/components/common/header/widgetHeader/WidgetHeader';
import { QInput } from '@ui/components/common/inputs/QInput';
import CustomSelect from '@ui/components/common/inputs/CustomSelect';
import HoursRegistrationApi from '@infrastructure/hours-registration-api';
import { burstSuccessSpark } from '@ui/components/common/micro-interactions';
import moment from 'moment';
import { clearHourDraft, currentActorId, draftStorageKey, hourWriteError, newHourId, readHourDraft, saveHourDraft } from '../hourWriteSafety';
import { hourRowsSnapshot } from '../hourRowsSnapshot';
import { formatDayLabel } from '../hours-dates';
// Mounted for one immutable selection. Background reads never reset the form
// or upgrade the version behind unsaved changes.
export function HoursEditor({ rows: source, allRows, context, label, reasons, onClose, onSaved, mode }) {
    const [snapshot] = useState(() => ({ context: { ...context }, allRows: allRows.map(r => ({ ...r })), reasons: [...reasons], label }));
    const [rows, setRows] = useState(() => source.map(row => ({
        id: row.id || newHourId(), date: moment(row.date).format('YYYY-MM-DD'),
        assignmentReasonId: row.assignmentReasonId || reasons.find(r => r.label === row.client)?.value || reasons.find(r => r.label === 'Workday')?.value || '',
        // Days without a saved registration start empty instead of a prefilled 0. Saved values are shown as stored.
        hours: row.isPersisted === false && !Number(row.hours) ? '' : String(row.hours ?? 0), isPersisted: !!row.isPersisted
    })));
    // Save is only offered when the rows differ from what was opened; reverting an edit makes the form clean again.
    const [initialSnapshot] = useState(() => hourRowsSnapshot(rows));
    const changed = hourRowsSnapshot(rows) !== initialSnapshot;
    const [weekVersion, setWeekVersion] = useState(source[0]?.weekVersion || '');
    const [actor] = useState(currentActorId);
    const [draftKey] = useState(() => {
        const prefix = `${context.assignmentId}/${context.year}/${context.month}/${context.weekNo}/${mode}/`;
        const date = moment(source[0]?.date).format('YYYY-MM-DD');
        const primary = draftStorageKey(actor, prefix + (mode === 'day' ? (source[0]?.isPersisted ? source[0].id : date) : ''));
        if (mode === 'day' && source[0]?.isPersisted && !readHourDraft(primary)) {
            // An uncertain create can now be persisted. Only follow the old date
            // key when it contains exactly this stable row, never another split.
            const fallback = draftStorageKey(actor, prefix + date);
            const previous = readHourDraft(fallback);
            if (previous?.rows.length === 1 && previous.rows[0].id === source[0].id)
                return fallback;
        }
        return primary;
    });
    const [draft, setDraft] = useState(() => readHourDraft(draftKey));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [mustReload, setMustReload] = useState(false);
    const [storageWarning, setStorageWarning] = useState(false);
    const lock = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const updateRows = (next) => {
        if (lock.current || draft)
            return;
        setRows(next);
        setDirty(true);
        setStorageWarning(!saveHourDraft(draftKey, { rows: next, weekVersion }));
    };
    const close = () => {
        if (lock.current)
            return;
        if (changed && !window.confirm('Discard the unsaved changes in this editor?'))
            return;
        if (dirty)
            clearHourDraft(draftKey);
        onClose(mustReload);
    };
    const restore = () => {
        if (!draft)
            return;
        setRows(draft.rows);
        setWeekVersion(draft.weekVersion);
        setDirty(true);
        setDraft(undefined);
        if (draft.weekVersion !== source[0]?.weekVersion) {
            setError('The saved week changed since this draft. Your draft is shown for review. It cannot replace the newer week; copy any needed values before closing and reloading.');
            setMustReload(true);
        }
    };
    const save = async () => {
        if (lock.current || mustReload)
            return;
        if (!weekVersion || !actor || currentActorId() !== actor) {
            setError('Sign in with the original account and reload this week before saving. Your input is kept.');
            setMustReload(true);
            return;
        }
        const totals = new Map();
        if (mode === 'day')
            snapshot.allRows.filter(r => r.id !== rows[0]?.id).forEach(r => {
                const date = moment(r.date).format('YYYY-MM-DD');
                totals.set(date, (totals.get(date) || 0) + r.hours);
            });
        for (const row of rows) {
            // An empty hours field is saved as 0.
            if (!row.assignmentReasonId || !/^\d*$/.test(row.hours.trim()) || Number(row.hours) > 17) {
                setError('Choose a registration and enter whole hours from 0 to 17 for each row. An empty field counts as 0.');
                return;
            }
            totals.set(row.date, (totals.get(row.date) || 0) + Number(row.hours));
        }
        if ([...totals.values()].some(n => n > 17)) {
            setError('A day may contain at most 17 hours across all registrations.');
            return;
        }
        lock.current = true;
        setSaving(true);
        setError('');
        setStorageWarning(!saveHourDraft(draftKey, { rows, weekVersion }));
        const fixedContext = snapshot.context;
        try {
            if (mode === 'day') {
                const row = rows[0];
                await HoursRegistrationApi.registerHoursPerDay({ ...fixedContext, id: row.id, assignmentReasonId: row.assignmentReasonId,
                    date: row.date, hours: Number(row.hours), expectedWeekVersion: weekVersion });
            }
            else {
                await HoursRegistrationApi.bulkRegisterHours(rows.map(row => ({ ...fixedContext, Id: row.id, assignmentReasonId: row.assignmentReasonId,
                    date: row.date, NoOfHours: Number(row.hours) })), weekVersion);
            }
            clearHourDraft(draftKey, { rows, weekVersion });
            if (mounted.current) {
                burstSuccessSpark();
                onSaved();
            }
        }
        catch (exception) {
            if (mounted.current) {
                const result = hourWriteError(exception);
                setError(result.message);
                setMustReload(result.mustReload);
            }
        }
        finally {
            lock.current = false;
            if (mounted.current)
                setSaving(false);
        }
    };
    return _jsxs(Dialog, { open: true, onClose: close, maxWidth: false, PaperProps: { sx: { m: { xs: 1.5, sm: 4 }, width: { xs: '100%', sm: 600 }, maxWidth: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' } } }, BackdropProps: { sx: { backdropFilter: 'blur(3px)' } }, children: [_jsxs(WidgetHeader, { background: 'white', children: [_jsxs(Typography, { title: `${snapshot.label} \u2014 Week ${snapshot.context.weekNo}`, sx: { fontSize: { xs: '15px', sm: '20px' }, lineHeight: 1.3, color: 'black', minWidth: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }, children: [snapshot.label, " \u2014 Week ", snapshot.context.weekNo] }), _jsx(IconButton, { disabled: saving, onClick: close, "aria-label": 'Close hours editor', children: _jsx(CloseCircle, { color: 'black' }) })] }), _jsxs(Box, { width: '100%', maxHeight: '75vh', overflowY: 'auto', overflowX: 'hidden', p: { xs: 2, sm: 3 }, children: [draft && _jsxs(Alert, { severity: 'info', children: ["Unsaved hours from this tab are available for up to two hours.", _jsx(Button, { onClick: restore, children: "Restore unsaved hours" }), _jsx(Button, { onClick: () => { clearHourDraft(draftKey); setDraft(undefined); }, children: "Discard draft" })] }), error && _jsx(Alert, { severity: 'error', role: 'alert', children: error }), !weekVersion && _jsx(Alert, { severity: 'warning', children: "Refresh this page to load the safe hours editor. Saving is disabled until the backend supplies a week version." }), storageWarning && _jsx(Alert, { severity: 'warning', children: "This browser cannot retain the draft through sign-in. Copy your input before leaving this page." }), _jsx(Grid, { container: true, spacing: 2, mt: 1, children: rows.map((row, index) => _jsxs(React.Fragment, { children: [_jsx(Grid, { item: true, xs: 12, sm: 3, sx: { pb: { xs: 0 } }, children: _jsx(Typography, { fontWeight: { xs: 500, sm: 400 }, children: formatDayLabel(row.date) }) }), _jsx(Grid, { item: true, xs: 6, sm: 5, children: _jsx(CustomSelect, { label: 'Registration', options: snapshot.reasons, selectedOption: row.assignmentReasonId, disabled: saving || !!draft, handleChange: (e) => updateRows(rows.map(r => r.id === row.id ? { ...r, assignmentReasonId: e.target.value } : r)) }) }), _jsx(Grid, { item: true, xs: 4, sm: 3, children: _jsx(QInput, { label: 'Hours', value: row.hours, disabled: saving || !!draft, onChange: (value) => updateRows(rows.map(r => r.id === row.id ? { ...r, hours: value } : r)) }) }), _jsx(Grid, { item: true, xs: 2, sm: 1, sx: { display: 'flex', alignItems: 'flex-end' }, children: mode === 'week' && (rows.findIndex(r => r.date === row.date) === index ?
                                        _jsx(IconButton, { "aria-label": `Add registration ${row.date}`, disabled: saving || !!draft, onClick: () => updateRows([...rows, {
                                                    id: newHourId(), date: row.date, hours: '', isPersisted: false,
                                                    assignmentReasonId: snapshot.reasons.find(r => r.label === 'Workday')?.value || ''
                                                }]), children: _jsx(Add, {}) }) :
                                        _jsx(IconButton, { "aria-label": `Remove registration ${row.date}`, disabled: saving || !!draft, onClick: () => updateRows(rows.filter(r => r.id !== row.id)), children: _jsx(Minus, {}) })) })] }, row.id)) }), _jsxs(Typography, { mt: 3, children: ["Total hours: ", rows.reduce((sum, row) => sum + (Number(row.hours) || 0), 0)] }), _jsx(Button, { "data-action-motion": "true", fullWidth: true, variant: 'contained', sx: { mt: 3 }, disabled: saving || mustReload || !weekVersion || !!draft || !rows.length || !changed, onClick: save, children: saving ? 'Saving hours…' : mode === 'day' ? 'Save hours' : 'Save weekly hours' }), mustReload && _jsx(Button, { onClick: () => onClose(true), children: "Keep draft and reload saved hours" })] })] });
}
